import React from 'react';
import Konva from 'konva';
import { Group, Rect, RegularPolygon, Text } from 'react-konva';

import { ROI_CONFIG, STROKE_COLOR } from '../config';

type DirectionIndicatorsProps = {
  x: number;
  y: number;
  height: number;
  rotation: number;
};

const RIGHT_COLOR = STROKE_COLOR;
const LEFT_COLOR = '#E54D4D';

const TOOLTIP_FONT_SIZE = 12;
const TOOLTIP_PADDING_X = 6;
const TOOLTIP_PADDING_Y = 4;
const TOOLTIP_BG_FILL = 'rgba(0, 0, 0, 0.85)';
const TOOLTIP_TEXT_FILL = '#fff';

export function DirectionIndicators({
  x,
  y,
  height,
  rotation,
}: DirectionIndicatorsProps) {
  const radius = height / 4 - 1;
  const [hovered, setHovered] = React.useState<1 | 2 | null>(null);
  const tooltipGroupRef = React.useRef<Konva.Group>(null);

  const tooltipText = hovered === 1 ? 'Direction 2' : 'Direction 1';

  // Konva doesn't support text padding natively; approximate tooltip size.
  const tooltipWidth =
    (hovered === null ? 0 : tooltipText.length) * (TOOLTIP_FONT_SIZE * 0.6) +
    TOOLTIP_PADDING_X * 2;
  const tooltipHeight = TOOLTIP_FONT_SIZE + TOOLTIP_PADDING_Y * 2;

  const tooltipX = x;
  const tooltipY = y - radius * 2.8 - tooltipHeight;

  React.useEffect(() => {
    if (hovered === null) {
      return;
    }

    // Ensure tooltip draws above other shapes in the same layer.
    tooltipGroupRef.current?.moveToTop();
    tooltipGroupRef.current?.getLayer()?.batchDraw();
  }, [hovered]);

  return (
    <>
      <RegularPolygon
        {...ROI_CONFIG.directionIndicator}
        x={x}
        y={y}
        fill={LEFT_COLOR}
        offsetY={4}
        radius={radius}
        rotation={rotation}
        stroke={LEFT_COLOR}
        onMouseEnter={() => setHovered(1)}
        onMouseLeave={() => setHovered((prev) => (prev === 1 ? null : prev))}
      />

      <RegularPolygon
        {...ROI_CONFIG.directionIndicator}
        x={x}
        y={y}
        fill={RIGHT_COLOR}
        offsetY={4}
        radius={radius}
        rotation={rotation}
        scaleY={-2}
        stroke={RIGHT_COLOR}
        onMouseEnter={() => setHovered(2)}
        onMouseLeave={() => setHovered((prev) => (prev === 2 ? null : prev))}
      />

      {hovered !== null && (
        <Group ref={tooltipGroupRef} listening={false}>
          <Rect
            x={tooltipX - tooltipWidth / 2}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            cornerRadius={4}
            fill={TOOLTIP_BG_FILL}
          />
          <Text
            x={tooltipX - tooltipWidth / 2 + TOOLTIP_PADDING_X}
            y={tooltipY + TOOLTIP_PADDING_Y}
            width={tooltipWidth - TOOLTIP_PADDING_X * 2}
            height={tooltipHeight - TOOLTIP_PADDING_Y * 2}
            fontSize={TOOLTIP_FONT_SIZE}
            fill={TOOLTIP_TEXT_FILL}
            text={tooltipText}
            align="center"
            verticalAlign="middle"
          />
        </Group>
      )}
    </>
  );
}
